import { FC, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { getProspectList } from '@/libs/fleet-api/services/prospect';
import FleetTitle from '@/libs/antd/components/Title';
// import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetPagination, FleetQueryParams } from '@/libs/fleet-api/fetcher';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const CCAProspectListPage: FC = () => {
	// const { navigate } = useApp();
	const { openCustomerDetail, openApprovalHistory } = useModals();

	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
	});

	const handleSearch = () => {
		setQueryParams((current) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			} else if (searchBy === 'status') {
				return {
					...current,
					search: undefined,
					reviewStatus: parseInt(searchValue),
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
			};
		});
	};

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getProspectList', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getProspectList(queryParams);
		},
	});

	if (isError) return <GenericErrorMessage />;

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>Prospects</FleetTitle>
			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={handleSearch}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={<span>Search By</span>}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								placeholder="Search By"
								options={[
									{
										label: 'Customer Name',
										value: 'customer-name',
									},
									{
										label: 'NPWP',
										value: 'npwp',
									},
									{
										label: 'Status',
										value: 'status',
									},
								]}
								style={{
									display: 'block',
									width: '100%',
								}}
								onChange={(value) => {
									setSearchValue(null);
									setSearchBy(value);
								}}
							/>
						</Form.Item>
					</Col>
					<Col style={{ width: 300 }}>
						<Form.Item>
							{searchBy === 'status' ? (
								<Select
									options={[
										{ label: 'New', value: '1' },
										{ label: 'Return', value: '2' },
										{ label: 'Reject', value: '3' },
									]}
									value={searchValue}
									onChange={(value) => {
										setSearchValue(value);
									}}
									placeholder="Select Status"
								/>
							) : (
								<Input
									placeholder="Search..."
									style={{
										display: 'block',
									}}
									disabled={searchBy === null}
									value={searchValue!}
									onChange={(e) => {
										setSearchValue(e.target.value);
									}}
								/>
							)}
						</Form.Item>
					</Col>
					<Col>
						<Form.Item>
							<Button
								block
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				bordered
				scroll={{ x: 'max-content' }}
				loading={isLoading || isFetching}
				dataSource={
					data?.data.items.map((record, key) => {
						return {
							...record,
							key,
						};
					}) ?? []
				}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setPagination((current) => {
							return {
								pageSize,
								pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
							};
						});
						setQueryParams((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
				columns={[
					{
						title: 'No',
						width: 1,
						render: (_, record) => record.no,
					},
					{
						title: 'Prospect ID',
						render: (_, record) => (
							<span title={record.status}>{record.prospectId}</span>
						),
					},
					{
						title: 'Customer Name',
						render: (_, { customerName, customerId }) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(customerId);
								}}
							>
								{customerName}
							</a>
						),
					},
					{
						title: 'Date of Input',
						render: (_, record) => record.prospectCreatedAt,
					},
					{
						title: 'Group Customer BMPP',
						render: (_, record) => record.customerGroupName ?? '-',
					},
					{
						title: 'Department',
						render: (_, record) => record.department,
					},
					{
						title: 'Fleet RM',
						render: (_, record) => record.frmName,
					},
					{
						title: 'Sektor Industri',
						render: (_, record) => record.industryClass ?? '-',
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openApprovalHistory(record.prospectId);
									}}
								>
									<span
										className={`review-status ${record.reviewStatus.toLowerCase()}`}
									>
										{record.reviewStatus.toLowerCase()}
									</span>
								</a>
							);
						},
					},
					{
						title: 'Pending User',
						render: (_, record) => record.pendingUser ?? '-',
					},
					// {
					// 	title: 'Select',
					// 	align: 'center',
					// 	render: () => (
					// 		<a
					// 			onClick={(e) => {
					// 				e.preventDefault();
					// 			}}
					// 			style={{ color: '#bababa' }}
					// 		>
					// 			<ArrowRightOutlined />
					// 		</a>
					// 	),
					// },
				]}
			/>
		</Space>
	);
};

export default CCAProspectListPage;
